import { useCallback, useEffect, useRef, useState } from 'react'

interface SessionConfig {
  enabled: boolean
  timeoutMinutes: number
  warningSeconds: number
}

/**
 * React hook that tracks user activity and shows a warning before session
 * idle timeout (STIG V-222389/V-222390).
 *
 * Fetches the effective timeout policy from `GET /api/session/config` (auth
 * required — returns admin-specific or default timeout based on caller's roles).
 *
 * Client-side timer is a UX hint; the server-side
 * {@link SessionIdleTimeoutMiddleware} is the enforcement point.
 */
export function useIdleTimeout() {
  const [config, setConfig] = useState<SessionConfig | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const lastActivityRef = useRef(Date.now())
  const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined)

  // Fetch session config on mount
  useEffect(() => {
    let cancelled = false
    const fetchConfig = async () => {
      try {
        const res = await fetch('/api/session/config', { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          if (!cancelled) setConfig(data)
        }
      } catch {
        // Config fetch failed — timeout not enforced client-side
      }
    }
    fetchConfig()
    return () => {
      cancelled = true
    }
  }, [])

  // Reset activity timer
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    if (showWarning) {
      setShowWarning(false)
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
  }, [showWarning])

  // Debounced activity tracking
  useEffect(() => {
    if (!config?.enabled) return

    let debounceTimer: ReturnType<typeof setTimeout>
    const onActivity = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => resetActivity(), 1000)
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const
    for (const event of events) {
      window.addEventListener(event, onActivity, { passive: true })
    }

    return () => {
      clearTimeout(debounceTimer)
      for (const event of events) {
        window.removeEventListener(event, onActivity)
      }
    }
  }, [config?.enabled, resetActivity])

  // Warning + countdown timer
  useEffect(() => {
    if (!config?.enabled || config.timeoutMinutes <= 0) return

    const timeoutMs = config.timeoutMinutes * 60 * 1000
    const warningMs = config.warningSeconds * 1000
    const warningAt = timeoutMs - warningMs

    const checkInterval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current
      if (elapsed >= warningAt && !showWarning) {
        setShowWarning(true)
        setSecondsLeft(Math.max(0, Math.ceil((timeoutMs - elapsed) / 1000)))

        // Start countdown
        countdownRef.current = setInterval(() => {
          const remaining = timeoutMs - (Date.now() - lastActivityRef.current)
          const secs = Math.max(0, Math.ceil(remaining / 1000))
          setSecondsLeft(secs)
        }, 1000)
      }
    }, 5000) // Check every 5 seconds

    return () => {
      clearInterval(checkInterval)
      const countdownId = countdownRef.current
      if (countdownId) clearInterval(countdownId)
    }
  }, [config, showWarning])

  /** Call this to reset the server-side timer (fetches /api/me). */
  const continueSession = useCallback(async () => {
    resetActivity()
    try {
      await fetch('/api/me', { credentials: 'include' })
    } catch {
      // If /api/me fails, the server already timed us out
    }
  }, [resetActivity])

  return {
    showWarning,
    secondsLeft,
    continueSession,
    enabled: config?.enabled ?? false,
  }
}
